import { useState } from "react";
import { addAdminNote, editAdminNote, removeAdminNote } from "@/lib/apiClient";
import { StickyNote, ChevronDown, Pencil, Trash2, Check, Plus, X } from "lucide-react";
import { toast } from "sonner";

const fmt = (s) => new Date(s).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
const stop = (e) => e.stopPropagation();

function NoteEditor({ initial = "", onSave, onCancel, testid }) {
  const [text, setText] = useState(initial);
  return (
    <div className="space-y-2" onClick={stop}>
      <textarea data-testid={testid} autoFocus value={text} onChange={(e) => setText(e.target.value)} rows={3} className="fe-input resize-y text-sm"
        placeholder="Bijv. 'Na 17:00 bellen', 'Klant wil eerst taxatie', 'Vorige keer besproken: …'" />
      <div className="flex gap-2">
        <button data-testid={`${testid}-save`} onClick={() => text.trim() && onSave(text)} className="px-3 py-1.5 rounded-lg bg-[#001C51] text-white text-xs font-medium hover:bg-[#0A337A]">Opslaan</button>
        <button onClick={onCancel} className="px-3 py-1.5 rounded-lg border border-[#E2E7EC] text-[#5B6675] text-xs hover:bg-[#EDF1F5]">Annuleren</button>
      </div>
    </div>
  );
}

// Admin instructions on a lead: compact chip in the lead list, expandable; employees can only read and close as done.
export function AdminNotes({ lead, isAdmin, onChanged }) {
  const notes = lead.admin_notes || [];
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const run = async (fn, msg) => { try { onChanged(await fn()); toast.success(msg); setEditId(null); } catch (e) { toast.error(e?.response?.data?.detail || "Mislukt"); } };
  const close = (n) => {
    const q = isAdmin ? "Deze notitie verwijderen?" : "Is deze notitie gelezen / afgehandeld? Dan verdwijnt hij voor het hele team.";
    if (window.confirm(q)) run(() => removeAdminNote(lead.id, n.id), isAdmin ? "Notitie verwijderd" : "Notitie afgehandeld");
  };
  if (!notes.length && !isAdmin) return null;
  if (!notes.length) {
    return editId === "new"
      ? <div className="basis-full" onClick={stop}><NoteEditor testid={`admin-note-new-${lead.id}`} onCancel={() => setEditId(null)} onSave={(t) => run(() => addAdminNote(lead.id, t), "Notitie toegevoegd")} /></div>
      : <button data-testid={`admin-note-add-${lead.id}`} onClick={(e) => { stop(e); setEditId("new"); }} title="Notitie voor het team"
          className="p-2 rounded-lg hover:bg-[#FFF4DB] text-[#B7791F] transition-colors"><StickyNote className="h-4 w-4" /></button>;
  }
  return (
    <div className="basis-full" onClick={stop} data-testid={`admin-notes-${lead.id}`}>
      <button data-testid={`admin-note-toggle-${lead.id}`} onClick={() => setOpen((o) => !o)}
        className="w-full text-left flex items-center gap-2 rounded-xl bg-[#FFF4DB] border border-[#F2D58A] px-3 py-2 text-sm text-[#7A5410] hover:bg-[#FCEBC0] transition-colors">
        <StickyNote className="h-4 w-4 shrink-0" />
        <span className="font-semibold shrink-0">Notitie van {notes[0].by_name || "admin"}{notes.length > 1 ? ` (+${notes.length - 1})` : ""}:</span>
        {!open && <span className="truncate">{notes[0].text}</span>}
        <ChevronDown className={`h-4 w-4 ml-auto shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {notes.map((n) => (
            <div key={n.id} data-testid={`admin-note-${n.id}`} className="rounded-xl border border-[#F2D58A] bg-white p-3">
              {editId === n.id ? <NoteEditor initial={n.text} testid={`admin-note-edit-${n.id}`} onCancel={() => setEditId(null)} onSave={(t) => run(() => editAdminNote(lead.id, n.id, t), "Notitie bijgewerkt")} /> : (
                <>
                  <p className="text-sm text-[#16202B] whitespace-pre-wrap">{n.text}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[11px] text-[#8994A3]">{n.by_name || n.by} · {fmt(n.at)}{n.updated_at ? " (bewerkt)" : ""}</span>
                    {isAdmin && <button data-testid={`admin-note-editbtn-${n.id}`} onClick={() => setEditId(n.id)} className="ml-auto p-1 rounded-lg text-[#001C51] hover:bg-[#EDF1F5]" title="Bewerken"><Pencil className="h-3.5 w-3.5" /></button>}
                    <button data-testid={`admin-note-close-${n.id}`} onClick={() => close(n)} className={`${isAdmin ? "" : "ml-auto "}inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs ${isAdmin ? "text-[#B0413E] hover:bg-[#F3DEDD]" : "text-[#0C6E6E] hover:bg-[#D6F3F3] font-medium"}`}>
                      {isAdmin ? <Trash2 className="h-3.5 w-3.5" /> : <><Check className="h-3.5 w-3.5" /> Gelezen / klaar</>}
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
          {isAdmin && (editId === "new"
            ? <NoteEditor testid={`admin-note-new-${lead.id}`} onCancel={() => setEditId(null)} onSave={(t) => run(() => addAdminNote(lead.id, t), "Notitie toegevoegd")} />
            : <button data-testid={`admin-note-add-${lead.id}`} onClick={() => setEditId("new")} className="inline-flex items-center gap-1 text-xs font-medium text-[#7A5410] hover:underline"><Plus className="h-3.5 w-3.5" /> Notitie toevoegen</button>)}
          <button onClick={() => setOpen(false)} className="inline-flex items-center gap-1 text-xs text-[#5B6675] hover:underline ml-3"><X className="h-3.5 w-3.5" /> Inklappen</button>
        </div>
      )}
    </div>
  );
}
